import { useEffect, useRef } from 'react';

export interface UseVisibilityAwarePollingOptions {
  /** Poll interval in ms. */
  intervalMs: number;
  /** Pause polling when document.visibilityState !== 'visible'. Default: true. */
  pauseWhenHidden?: boolean;
  /** Run the callback once immediately on mount. Default: true. */
  immediate?: boolean;
  /** Disable polling entirely when false (useful for conditional wiring). Default: true. */
  enabled?: boolean;
}

/**
 * Runs `callback` every `intervalMs` ms. Pauses automatically when the
 * tab is hidden so mobile devices don't drain battery on idle polls.
 */
export function useVisibilityAwarePolling(
  callback: () => void | Promise<void>,
  options: UseVisibilityAwarePollingOptions,
): void {
  const cbRef = useRef(callback);
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const { intervalMs, pauseWhenHidden = true, immediate = true, enabled = true } = options;
    if (!enabled || intervalMs <= 0) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer) return;
      if (immediate) void cbRef.current();
      timer = setInterval(() => {
        void cbRef.current();
      }, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVis = () => {
      if (!pauseWhenHidden) return;
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    if (!pauseWhenHidden || document.visibilityState === 'visible') start();
    if (pauseWhenHidden) document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      if (pauseWhenHidden) document.removeEventListener('visibilitychange', onVis);
    };
    // We deliberately depend on the primitive option fields rather than the
    // whole `options` object — callers usually pass an inline object literal,
    // which would otherwise restart the interval every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.intervalMs, options.pauseWhenHidden, options.immediate, options.enabled]);
}
